import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useCallback, useMemo } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import { useDispatch, useSelector } from "react-redux";
import PageContainer from "../../common_components/PageContainer";
import Pagination from "../../common_components/Pagination";
import ConfirmActionModal from "../../common_components/ConfirmActionModal";
import NotesModal from "../../common_components/NotesModal";
import useToast from "../../common_components/Toast/useToast";
import { fetchOptOutContacts, fetchOptOutContactById, createOptOutContact, updateOptOutContact, deleteOptOutContact, selectOptOutContacts, selectOptOutContactPagination, selectOptOutContactLoading, } from "../../redux/optOutContactSlice";
import OptOutContactsHeader from "./Component/OptOutContactsHeader";
import OptOutContactsMap from "./Component/OptOutContactsMap";
import AddEditDrawer from "./Drawer/AddEditDrawer";
import { OPT_OUT_SORT_MAP, mapContactsFromApi, } from "./utils";
import "../../styles/OptOutContacts.css";
const ITEMS_PER_PAGE = 20;
export default function OptOutContactsMain() {
    const toast = useToast();
    const dispatch = useDispatch();
    const contacts = useSelector(selectOptOutContacts);
    const pagination = useSelector(selectOptOutContactPagination);
    const loading = useSelector(selectOptOutContactLoading);
    const [activeTab, setActiveTab] = useState("active");
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({
        columnId: "created_at",
        direction: "desc",
    });
    const [searchText, setSearchText] = useState("");
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedContact, setSelectedContact] = useState(null);
    const [editLoadingId, setEditLoadingId] = useState(null);
    const [deleteState, setDeleteState] = useState({ open: false, loading: false, payload: null });
    const [notesState, setNotesState] = useState({ open: false, record: null });
    // Table feature state
    const [hiddenColumns, setHiddenColumns] = useState(new Set());
    const [columnVisibilityAnchor, setColumnVisibilityAnchor] = useState(null);
    const [columnFilters, setColumnFilters] = useState({});
    const [columnOrder, setColumnOrder] = useState(null);
    const [showFilterRow, setShowFilterRow] = useState(false);
    const [activeFilterColumnId, setActiveFilterColumnId] = useState(null);
    const [pinnedColumns, setPinnedColumns] = useState({
        left: [],
        right: [],
    });
    const rows = useMemo(() => mapContactsFromApi(contacts), [contacts]);
    const activeCount = pagination?.active || 0;
    const inactiveCount = pagination?.inactive || 0;
    const totalPages = Number(pagination?.totalPages || 1);
    // The module is access-only (no granular actions), so anyone who can reach
    // this page may manage its records.
    const canManage = true;
    const fetchContacts = useCallback(() => {
        const status = activeTab === "active" ? "active" : "inactive";
        const apiSortBy = sortConfig?.columnId && sortConfig.direction
            ? OPT_OUT_SORT_MAP[sortConfig.columnId] || "createdAt"
            : "createdAt";
        const effectiveSearch = columnFilters?.name || searchText;
        dispatch(fetchOptOutContacts({
            page: currentPage,
            limit: ITEMS_PER_PAGE,
            status,
            searchText: effectiveSearch || undefined,
            sortBy: apiSortBy,
            sortOrder: sortConfig?.direction || "desc",
        }));
    }, [dispatch, activeTab, currentPage, sortConfig, searchText, columnFilters]);
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchContacts();
        }, 400);
        return () => clearTimeout(timer);
    }, [fetchContacts]);
    const handleSearchChange = (text) => {
        setSearchText(text);
        setCurrentPage(1);
    };
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setCurrentPage(1);
    };
    const handleColumnVisibilityClick = (event) => {
        setColumnVisibilityAnchor(event.currentTarget);
    };
    const handleColumnFilterChange = (columnId, filterValue) => {
        setColumnFilters((prev) => {
            const next = { ...prev };
            if (filterValue)
                next[columnId] = filterValue;
            else
                delete next[columnId];
            return next;
        });
        setCurrentPage(1);
    };
    const handleFilterRowToggle = (payload = null) => {
        if (!payload || payload?.mode === "all") {
            setActiveFilterColumnId(null);
            setShowFilterRow((prev) => payload?.open !== undefined ? payload.open : !prev);
            return;
        }
        if (payload?.mode === "single" && payload?.columnId) {
            if (payload?.open === false) {
                setActiveFilterColumnId(null);
                setShowFilterRow(false);
                return;
            }
            setActiveFilterColumnId(payload.columnId);
            setShowFilterRow(true);
        }
    };
    const handleToolbarFilterToggle = () => {
        setShowFilterRow((prev) => {
            const next = !prev;
            if (!next)
                setActiveFilterColumnId(null);
            return next;
        });
    };
    const handleSortChange = (columnId, direction) => {
        if (!columnId || !direction) {
            setSortConfig({ columnId: null, direction: "asc" });
        }
        else {
            setSortConfig({ columnId, direction });
        }
        setCurrentPage(1);
    };
    const handleEdit = async (contact) => {
        try {
            setEditLoadingId(contact.id);
            const fetched = await dispatch(fetchOptOutContactById(contact.id)).unwrap();
            setSelectedContact({
                id: fetched._id,
                name: fetched.name || "",
                email: fetched.email || "",
                status: fetched.status || "active",
                notes: fetched.notes || "",
            });
            setDrawerOpen(true);
        }
        catch (error) {
            toast.error(error || "Something went wrong!");
        }
        finally {
            setEditLoadingId(null);
        }
    };
    const handleDelete = (row) => {
        setDeleteState({ open: true, loading: false, payload: row });
    };
    const handleNotes = (row) => {
        setNotesState({ open: true, record: row });
    };
    const handleSaveNotes = async (notes) => {
        if (!notesState.record)
            return;
        try {
            await dispatch(updateOptOutContact({ id: notesState.record.id, payload: { notes } })).unwrap();
            toast.success("Notes updated successfully.");
            setNotesState({ open: false, record: null });
            fetchContacts();
        }
        catch (error) {
            toast.error(error || "Failed to update notes");
            throw error;
        }
    };
    const confirmDelete = async () => {
        const contact = deleteState.payload;
        if (!contact)
            return;
        setDeleteState((p) => ({ ...p, loading: true }));
        try {
            await dispatch(deleteOptOutContact(contact.id)).unwrap();
            toast.success("Opt-out contact deleted successfully.");
            setDeleteState({ open: false, loading: false, payload: null });
            fetchContacts();
        }
        catch (error) {
            toast.error(error || "Delete failed");
            setDeleteState((p) => ({ ...p, loading: false }));
        }
    };
    const handleAddNewContact = () => {
        setSelectedContact(null);
        setDrawerOpen(true);
    };
    const handleDrawerClose = () => {
        setDrawerOpen(false);
        setSelectedContact(null);
    };
    const handleSaveContact = async (values) => {
        const payload = {
            name: values?.name || "",
            email: values?.email || "",
            status: values?.status || "active",
            notes: values?.notes || "",
        };
        try {
            if (selectedContact) {
                await dispatch(updateOptOutContact({ id: selectedContact.id, payload })).unwrap();
                toast.success("Opt-out contact updated successfully.");
            }
            else {
                await dispatch(createOptOutContact(payload)).unwrap();
                toast.success("Opt-out contact created successfully.");
            }
            setDrawerOpen(false);
            setSelectedContact(null);
            fetchContacts();
            return true;
        }
        catch (error) {
            const errorMessage = typeof error === "string" ? error : error?.message || "Save failed";
            toast.error(errorMessage);
            if (errorMessage.includes("email already exists")) {
                return { errors: { email: errorMessage } };
            }
            return false;
        }
    };
    return (_jsxs(PageContainer, { children: [_jsx(Box, { className: "user-list-container opted-out-contacts-module", children: _jsxs(Paper, { className: "user-list-card", elevation: 0, children: [_jsx(OptOutContactsHeader, { activeTab: activeTab, onTabChange: handleTabChange, activeCount: activeCount, inactiveCount: inactiveCount, searchText: searchText, onSearchChange: handleSearchChange, onColumnVisibilityClick: handleColumnVisibilityClick, showFilterRow: showFilterRow, onFilterRowToggle: handleToolbarFilterToggle, onAddClick: handleAddNewContact, canManage: canManage }), _jsx(OptOutContactsMap, { rows: rows, loading: loading, searchText: searchText, editLoadingId: editLoadingId, canManage: canManage, onEdit: handleEdit, onNotes: handleNotes, onDelete: handleDelete, sortConfig: sortConfig, onSortChange: handleSortChange, hiddenColumns: hiddenColumns, onColumnVisibilityChange: setHiddenColumns, columnVisibilityAnchor: columnVisibilityAnchor, onColumnVisibilityClose: () => setColumnVisibilityAnchor(null), columnFilters: columnFilters, onColumnFilterChange: handleColumnFilterChange, showFilterRow: showFilterRow, activeFilterColumnId: activeFilterColumnId, onActiveFilterColumnIdChange: setActiveFilterColumnId, onFilterRowToggle: handleFilterRowToggle, columnOrder: columnOrder, onColumnOrderChange: setColumnOrder, pinnedColumns: pinnedColumns, onColumnPinningChange: setPinnedColumns }), totalPages > 1 && (_jsx(Box, { className: "user-list-pagination-wrapper", sx: { paddingInline: "14px" }, children: _jsx(Pagination, { currentPage: currentPage, totalPages: totalPages, onPageChange: setCurrentPage, maxVisiblePages: 5, totalEntries: activeTab === "active" ? activeCount : inactiveCount, itemsPerPage: ITEMS_PER_PAGE, showEntriesMessage: true }) }))] }) }), drawerOpen && (_jsx(AddEditDrawer, { open: drawerOpen, onClose: handleDrawerClose, contactData: selectedContact, onSave: handleSaveContact })), deleteState?.open && (_jsx(ConfirmActionModal, { className: "delete-popup-container", open: deleteState.open, loading: deleteState.loading, title: "Are You Sure?", description: "You\u2019re about to remove this opt-out contact. Once deleted, this action cannot be undone.", confirmText: "Yes, Proceed", cancelText: "No, Cancel", onClose: () => !deleteState.loading &&
                    setDeleteState({ open: false, loading: false, payload: null }), onConfirm: confirmDelete })), notesState.open && (_jsx(NotesModal, { open: notesState.open, onClose: () => setNotesState({ open: false, record: null }), initialNotes: notesState.record?.notes || "", onSave: handleSaveNotes }))] }));
}
